/*
 * @Author: wblijinyang
 * @Description:
 * 商品属性面板组件，同时支持 attr & mmu 两种模式
 */
import { cls } from 'reactutils';
import InputNumberComp from '@/comps/InputNumber';
import { MmuModeType, MmuDataType } from 'libs/enums';
import { useState, useEffect, Fragment } from 'react';
import { Popover } from 'antd';
import type { IProductDetail } from '../../product';
interface ProductDetailAttributeProps {
  attributeData:
    | IProductDetail['attributeList'][number]
    | IProductDetail['mmuAttributeTagList'][number];
  isMmu: boolean;
  handleChangeMmu?: Function;
  handleChangeSku?: Function;
}
// NOTE: 该组件直接修改 attributeData 的值
const ProductDetailAttribute = ({
  attributeData,
  isMmu = true,
  handleChangeMmu,
  handleChangeSku,
}: ProductDetailAttributeProps) => {
  const [attribute, setAttribute] = useState(attributeData);
  const isRadio = !isMmu || (!attribute.hasCustomize && attribute.modeType === MmuModeType.RADIO);
  const isCheckbox = !attribute.hasCustomize && attribute.modeType === MmuModeType.CHECKBOX;
  const isTEXT = attribute.hasCustomize && attribute.dataType === MmuDataType.TEXT;
  const isFloat = attribute.hasCustomize && attribute.dataType === MmuDataType.FLOAT;
  const isInt = attribute.hasCustomize && attribute.dataType === MmuDataType.INT;

  useEffect(() => {
    if (isRadio || isCheckbox) {
      setAttribute(attributeData);
    } else {
      // 兼容处理，当前选中
      if (attributeData.categoryAttributeValueList) {
        // 处理 state 和 props 的同步, 渲染是靠 state
        // input 输入框初始化值，设置为后端接口中返回的值
        attributeData.categoryAttributeValueList[0].attributeValue =
          attribute.categoryAttributeValueList[0].attributeValue;
      }
      setAttribute(attributeData);
    }
  }, [attributeData]);

  const radioAttrItemClass = (attr) => {
    return radioAttrItemClassWithSkus(attr, undefined, false);
  };
  const radioAttrItemClassWithSkus = (attr, skuIndex, nonBottom) => {
    var canCancel;
    if (skuIndex === undefined) {
      if (isMmu) {
        canCancel = !attribute.hasRequired;
      } else {
        canCancel = !!attr.cancelAttributeSkuSysNo;
      }
    }
    if (skuIndex !== undefined) {
      return cls(
        nonBottom ? 'product-filter-item product-filter-item-non-bottom' : 'product-filter-item',
        attr.skus[skuIndex].selected && 'active',
        attr.disable && 'disabled',
        attr.skus[skuIndex].selected && 'product-filter-item-can-cancel',
      );
    }
    return cls(
      'product-filter-item',
      attr.active && 'active',
      attr.disable && 'disabled',
      canCancel && 'product-filter-item-can-cancel',
    );
  };
  const handleClickRadio = (attr, curIndex) => {
    handleClickRadioWithSku(attr, curIndex, undefined);
  };
  const handleClickRadioWithSku = (attr, curIndex, skuIndex) => {
    if (skuIndex !== undefined) {
      for (let index = 0; index < attr.skus.length; index++) {
        if (skuIndex !== index) {
          attr.skus[index].selected = false;
        } else {
          attr.skus[index].selected = true;
          handleChangeSku(attr.skus[index].sysNo);
        }
      }
      return;
    }
    if (isMmu) {
      if (attr.active) {
        if (attribute.hasRequired) return;
        attr.active = false;
        handleChangeMmu();
      } else {
        attribute.categoryAttributeValueList.forEach((attrs, index) => {
          attrs.active = index === curIndex;
        });
        handleChangeMmu();
      }
      setAttribute({
        ...attribute,
      });
    } else {
      if (attr.active) {
        if (attr.cancelAttributeSkuSysNo !== null) {
          attr.active = false;
          handleChangeSku(attr.cancelAttributeSkuSysNo);
        }
      } else {
        attribute.categoryAttributeValueList.forEach((attrs, index) => {
          attrs.active = index === curIndex;
        });
        handleChangeSku(attr.skuSysNo);
      }
      setAttribute({
        ...attribute,
      });
    }
  };

  const handleClickCheckbox = (attr) => {
    attr.active = !attr.active;
    setAttribute({
      ...attribute,
    });
    handleChangeMmu();
  };
  const handleInput = (e) => {
    var val = e.target.value || '';
    val = isFloat ? val.replace(/^\D*(\d*(?:\.\d{0,4})?).*$/g, '$1') : val && val.trim();
    if (val !== null && val !== undefined && val !== '') {
      attribute.categoryAttributeValueList[0].active = true;
    } else {
      attribute.categoryAttributeValueList[0].active = false;
    }
    attribute.categoryAttributeValueList[0].attributeValue = val;
    setAttribute({
      ...attribute,
    });
  };
  const handleInputInt = () => {
    var val = attribute.attributeValue;
    if (val && val !== '') {
      attribute.categoryAttributeValueList[0].active = true;
    } else {
      attribute.categoryAttributeValueList[0].active = false;
    }
    setAttribute({
      ...attribute,
    });
    handleChangeMmu();
  };

  return (
    <div>
      {isRadio ? (
        <div className="vue-product-detail-attribute-radio">
          {attribute.categoryAttributeValueList?.map((attr, attrIndex) => (
            <Fragment key={attrIndex}>
              {attr.skus && attr.skus.length > 1 ? (
                <Popover
                  className="vue-product-detail-attribute-radio"
                  content={attr.skus.map((sku, skuIndex) => {
                    return (
                      <span
                        className={radioAttrItemClassWithSkus(attr, skuIndex, true)}
                        title={sku.newFlag === 1 ? '新商品' : '旧商品'}
                        onClick={() => {
                          if (!attr.disable) {
                            handleClickRadioWithSku(attr, attrIndex, skuIndex);
                          }
                        }}
                        key={skuIndex}
                      >
                        {attr.skus[skuIndex].selected && <div className="active-icon" />}
                        {attr.attributeValue + (sku.newFlag === 1 ? '(新)' : '(旧)')}
                      </span>
                    );
                  })}
                  trigger="hover"
                >
                  {attr.skus.map((sku, skuIndex) => {
                    return attr.skus[skuIndex].selected ||
                      (attr.skus.filter((item) => !item.selected).length == attr.skus.length &&
                        skuIndex === 0) ? (
                      <span
                        className={radioAttrItemClassWithSkus(attr, skuIndex, true)}
                        title={attr.attributeValue}
                        key={skuIndex + attrIndex}
                      >
                        {attr.skus[skuIndex].selected && <div className="active-icon" />}
                        {attr.attributeValue + (attr.skus[skuIndex].newFlag ? '(新)' : '(旧)')}
                      </span>
                    ) : (
                      <></>
                    );
                  })}
                </Popover>
              ) : (
                <span
                  className={radioAttrItemClass(attr)}
                  title={attr.attributeValue}
                  onClick={() => {
                    handleClickRadio(attr, attrIndex);
                  }}
                  key={attrIndex}
                >
                  {attr.active && <div className="active-icon" />}
                  {attr.attributeValue}
                </span>
              )}
            </Fragment>
          ))}
        </div>
      ) : isCheckbox ? (
        <div className="vue-product-detail-attribute-checkbox">
          {attribute.categoryAttributeValueList?.map((attr, attrIndex) => (
            <div
              className="chebox-item"
              key={attrIndex}
              onClick={() => {
                handleClickCheckbox(attr);
              }}
            >
              <i className={cls('iconfont checkbox', attr.active && 'checked')} />
              <span className="name">{attr.attributeValue}</span>
            </div>
          ))}
        </div>
      ) : isTEXT || isFloat ? (
        <div className="vue-product-detail-attribute-customize">
          {attribute.categoryAttributeValueList[0].prefix && (
            <span className="attribute-customize-prefix">
              {attribute.categoryAttributeValueList[0]?.prefix}
            </span>
          )}

          <input
            value={attribute.categoryAttributeValueList[0].attributeValue}
            className="attribute-input"
            onInput={handleInput}
            onBlur={() => {
              handleChangeMmu();
            }}
          />
          {attribute.categoryAttributeValueList[0].suffix && (
            <span className="attribute-customize-prefix">
              {attribute.categoryAttributeValueList[0].suffix}
            </span>
          )}
        </div>
      ) : isInt ? (
        <InputNumberComp
          value={attribute.attributeValue}
          onChange={(value) => {
            attribute.attributeValue = value;
            handleInputInt();
          }}
        />
      ) : (
        ''
      )}
      <style jsx>{`
        .vue-product-detail-attribute-customize {
          display: flex;
          align-items: center;
          margin-bottom: 10px;
        }
        .vue-product-detail-attribute-customize .attribute-customize-prefix {
          font-size: 12px;
          color: #666666;
          line-height: 1;
          margin-right: 8px;
        }
        .vue-product-detail-attribute-customize .attribute-customize-prefix {
          font-size: 12px;
          color: #666666;
          line-height: 1;
          margin-left: 8px;
        }
        .vue-product-detail-attribute-customize .attribute-input {
          background: #FFFFFF;
          border: 1px solid #D6D6D6;
          width: 76px;
          height: 28px;
          line-height: 28px;
          padding: 0 4px;
        }
        .vue-product-detail-attribute-checkbox {
          display: flex;
          align-items: center;
          margin-bottom: 5px;
          padding-top: 5px;
          flex-wrap: wrap;
        }
        .vue-product-detail-attribute-checkbox .chebox-item {
          display: flex;
          align-items: center;
          margin-right: 20px;
          cursor: pointer;
          margin-bottom: 5px;
          font-size: 12px;
        }
      
        .vue-product-detail-attribute-checkbox .chebox-item:last-child {
          margin-right: 0;
        }
      
        .vue-product-detail-attribute-checkbox .chebox-item .name {
          margin-left: 6px;
        }
      
        .checkbox {
          display: inline-block;
          width: 16px;
          height: 16px;
          line-height: 16px;
          text-align: center;
          background-color: #fff;
          border: 1px solid #e8e8e8;
          cursor: pointer;
          font-size: 12px;
        }
        .checkbox.checked {
          color: #fff;
          background-color: var(--rv-primary-color);
          border-color: var(--rv-primary-color);
        }
        .checkbox.checked:before {
          content: "\e607";
        }
        .product-filter-item-can-cancel.active, .product-filter-item-can-cancel:hover {
          cursor: pointer;
        }

        .product-filter-item {
          display: inline-block;
          padding: 9px 13px 7px 14px;
          font-size: 12px;
          line-height: 12px;
          color: #666;
          border: 1px solid #e8e8e8;
          margin-bottom: 10px;
          margin-right: 10px;
          position: relative;
          cursor: pointer;
        }
        .product-filter-item-non-bottom {
          margin-bottom: 0px !important;
        }

        .product-filter-item.active, .product-filter-item.category-item:hover {
          border-color: var(--rv-primary-color);
          cursor: auto;
        }
        .vue-product-detail-attribute-customize .attribute-input {
          background: #FFFFFF;
          border: 1px solid #D6D6D6;
          width: 76px;
          height: 28px;
          line-height: 28px;
          padding: 0 4px;
        }
        .active-icon {
          position: absolute;
          right: -8px;
          bottom: -8px;
          border: 8px solid transparent;
          border-top: 8px solid #178fd5;
          transform: rotate(-45deg);
        }
        .active-icon:after {
          content: "";
          position: absolute;
          left: -2px;
          top: -7px;
          width: 6px;
          height: 3px;
          border-color: transparent transparent #fff #fff;
          border-style: solid;
          border-width: 1px;
        }
        .product-filter-item.disabled {
          background-color: #f9f9f9;
          border-color: #f1f1f1;
          color: #aaa;
        }
        .product-filter-item.disabled:hover {
          border: 1px dashed var(--rv-primary-color);
          background-color: #fff;
          color: #333;
        }
      `}</style>
    </div>
  );
};

export default ProductDetailAttribute;
